import {useEffect,useLayoutEffect,useRef,useState,type ReactNode} from 'react';
import {ToolIcon} from './PaintingDock';

export interface EffectChoice {
  id:string; label:string; enabled:boolean; description:string;
  onToggle:(enabled:boolean)=>void; settings?:ReactNode;
}

/** Disclosure state belongs here; all effect values stay with the existing tool. */
export function EffectsPanel({effects,locked=false,onClose,onReset}:{effects:EffectChoice[];locked?:boolean;onClose:()=>void;onReset:()=>void}) {
  const [selected,setSelected]=useState(effects[0]?.id);
  const [anchorRight,setAnchorRight]=useState<number>();
  const panel=useRef<HTMLElement>(null),close=useRef(onClose),trigger=useRef<HTMLElement|null>(null);
  close.current=onClose;
  const closeAndFocus=()=>{close.current();trigger.current?.focus();};
  const current=effects.find(effect=>effect.id===selected)??effects[0];
  useLayoutEffect(()=>{
    const element=panel.current,parent=element?.offsetParent,button=element?.parentElement?.querySelector<HTMLElement>('[data-effects-trigger]');
    if(!element||!(parent instanceof HTMLElement)||!button)return;
    const place=()=>{
      const width=element.getBoundingClientRect().width;
      const right=Math.min(window.innerWidth-16,Math.max(width+16,button.getBoundingClientRect().right));
      setAnchorRight(parent.getBoundingClientRect().right-right);
    };
    place();
    const observer=new ResizeObserver(place);observer.observe(element);observer.observe(parent);observer.observe(button);
    window.addEventListener('resize',place);window.addEventListener('scroll',place,true);
    return()=>{observer.disconnect();window.removeEventListener('resize',place);window.removeEventListener('scroll',place,true);};
  },[]);
  useEffect(()=>{
    // Safari pointer activation may leave activeElement on the previous control.
    const previous=panel.current?.parentElement?.querySelector<HTMLElement>('[data-effects-trigger]')??document.activeElement as HTMLElement|null;
    trigger.current=previous;
    panel.current?.querySelector<HTMLButtonElement>('.effect-select')?.focus();
    const key=(event:KeyboardEvent)=>{
      if(event.key==='Escape'&&!event.defaultPrevented){event.preventDefault();event.stopPropagation();close.current();previous?.focus();}
    };
    const outside=(event:PointerEvent)=>{
      const target=event.target as Element;
      if(!panel.current?.contains(target)&&!target.closest('[data-effects-trigger]'))close.current();
    };
    document.addEventListener('keydown',key);document.addEventListener('pointerdown',outside);
    return()=>{document.removeEventListener('keydown',key);document.removeEventListener('pointerdown',outside);};
  },[]);
  return <section ref={panel} className="desktop-property-popover effects-panel" style={anchorRight===undefined?undefined:{right:anchorRight}} role="dialog" aria-label="Effects" onPointerDown={event=>event.stopPropagation()} onWheel={event=>event.stopPropagation()}>
    <header><div><strong>Effects</strong><small>{effects.filter(effect=>effect.enabled).length} on · Applied to your next mark</small></div><button type="button" aria-label="Close effects" onClick={closeAndFocus}><ToolIcon name="close"/></button></header>
    <div className="effects-body"><div className="effects-list" role="group" aria-label="Available effects">{effects.map(effect=><div className="effect-row" key={effect.id} data-selected={current?.id===effect.id}>
      <button type="button" className="effect-select" aria-pressed={current?.id===effect.id} onClick={()=>setSelected(effect.id)}>{effect.label}</button>
      <button type="button" className="effect-switch" role="switch" aria-label={effect.label} aria-checked={effect.enabled} disabled={locked} onClick={()=>effect.onToggle(!effect.enabled)}><span/></button>
    </div>)}</div><div className="effect-editor" role="region" aria-label={current?`${current.label} settings`:'Effect settings'}>{current&&<><h3>{current.label}</h3><p>{current.description}</p>{current.settings??<p className="effect-simple-note">{current.enabled?'On':'Off'} · No additional settings</p>}</>}</div></div>
    <footer><span>Settings never change earlier marks.</span><button type="button" disabled={locked} onClick={onReset}><ToolIcon name="reset"/>Reset effects</button></footer>
  </section>;
}
